import { useAuth } from "@clerk/nextjs";
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, X } from "lucide-react";
import { usePlausible } from "next-plausible";
import posthog from "posthog-js";
import toast from "react-hot-toast";
import {
	EmailIcon,
	EmailShareButton,
	FacebookIcon,
	FacebookShareButton,
	LinkedinIcon,
	LinkedinShareButton,
	PocketIcon,
	PocketShareButton,
	RedditIcon,
	RedditShareButton,
	TwitterIcon,
	TwitterShareButton,
	WhatsappIcon,
	WhatsappShareButton,
} from "react-share";
import { useCopyToClipboard } from "react-use";

import useQueryParam from "~/hooks/useQueryParam";

const ShareModal = () => {
	const [, copy] = useCopyToClipboard();

	const [shareParam, setShareParam] = useQueryParam("share");

	const { userId, isLoaded } = useAuth();

	const referrerId = isLoaded && userId;

	const refererParam = referrerId ? `?referrer_id=${referrerId}` : "";

	const basePath = process.env.NEXT_PUBLIC_SITE_URL!;

	const url = shareParam?.startsWith("http")
		? shareParam
		: `${basePath}${shareParam}${refererParam}`;
	const track = usePlausible();

	const socials = [
		{
			Component: EmailShareButton,
			Icon: EmailIcon,
			label: "Email",
		},

		{
			Component: FacebookShareButton,
			Icon: FacebookIcon,
			label: "Facebook",
		},

		{
			Component: TwitterShareButton,
			Icon: TwitterIcon,
			label: "Twitter",
		},

		{
			Component: WhatsappShareButton,
			Icon: WhatsappIcon,
			label: "WhatsApp",
		},

		{
			Component: RedditShareButton,
			Icon: RedditIcon,
			label: "Reddit",
		},

		{
			Component: LinkedinShareButton,
			Icon: LinkedinIcon,
			label: "LinkedIn",
		},
		{
			Component: PocketShareButton,
			Icon: PocketIcon,
			label: "Pocket",
		},
	];

	return (
		<AnimatePresence>
			{shareParam && (
				<Dialog
					key="share-dialog"
					static
					open={!!shareParam}
					onClose={() => setShareParam(null)}
					className="relative z-50"
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 bg-black/40 dark:bg-black/50"
						aria-hidden="true"
					/>

					<div className="fixed inset-0 overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-2 sm:p-10">
							<Dialog.Panel
								as={motion.div}
								initial={{ opacity: 0, y: 15 }}
								animate={{
									opacity: 1,
									y: 0,
									transition: { duration: 0.2 },
								}}
								exit={{ opacity: 0, y: 15, transition: { duration: 0.15 } }}
								key="share-dialog-content"
								className="relative mx-auto flex w-full max-w-xl flex-col gap-5 rounded-2xl bg-white px-6 pt-5 pb-6 shadow-2xl shadow-black/10 sm:gap-5 sm:px-8 sm:py-6 dark:bg-slate-800 dark:shadow-slate-900"
							>
								<div className="flex justify-between">
									<Dialog.Title className="font-medium text-2xl">
										Share
									</Dialog.Title>
									<button
										type="button"
										className="relative cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:brightness-95 sm:-right-1 dark:bg-slate-700"
										onClick={() => setShareParam(null)}
									>
										<X />
									</button>
								</div>

								{userId && (
									<p className="mb-1 rounded-xl border border-sky-700/20 bg-gradient-to-r from-sky-50 to-amber-50 px-4 py-2 font-medium dark:border-sky-200/10 dark:from-sky-300/20 dark:to-amber-300/20">
										Share content and you could earn up to $50 cashback.
									</p>
								)}

								<div
									onClickCapture={() => {
										track("Share", {
											props: { url: shareParam },
										});
									}}
									className="grid grid-cols-4 gap-4 sm:grid-cols-5 sm:gap-6"
								>
									<button
										className="flex flex-col items-center justify-center gap-1 hover:brightness-95"
										onClick={() => {
											copy(url);
											toast.success("URL Copied to Clipboard");

											posthog.capture("share", {
												content_path: shareParam,
												destination: "clipboard",
											});
										}}
									>
										<div className="flex h-[56px] w-[56px] items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
											<Copy size={24} className="shrink-0" />{" "}
										</div>
										<span className="text-sm sm:text-inherit">Copy URL</span>
									</button>

									{socials.map(({ Component, Icon, label }) => (
										<Component
											url={url}
											onClick={() => {
												posthog.capture("share", {
													content_path: shareParam,
													destination: label,
												});
											}}
											className="flex flex-col items-center gap-1 bg-slate-200 hover:brightness-90"
											key={label}
										>
											<Icon size={56} round className="fill-white" />
											<span className="text-sm sm:text-inherit">{label}</span>
										</Component>
									))}
								</div>
							</Dialog.Panel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default ShareModal;
